import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Table from '../components/Table';
import Button from '../components/Button';
import SearchBar from '../components/SearchBar';
import { applicationsApi } from '../services/api';
import { badgeStyles } from '../styles/sharedStyles';

const Dashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsApi.getAll();
      const rawData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.applications || response.data?.data || []);

      // Normalize keys to handle lowercase, snake_case, and camelCase database schemas seamlessly
      const normalized = rawData.map((app) => ({
        ...app,
        id: app.id,
        firstName: app.firstName || app.firstname || app.first_name || '',
        lastName: app.lastName || app.lastname || app.last_name || '',
        email: app.email || '',
        areaOfSpecialty: app.areaOfSpecialty || app.areaofspecialty || app.area_of_specialty || app.specialty || '',
        status: app.status || 'pending',
        createdAt: app.createdAt || app.createdat || app.created_at || new Date()
      }));

      setApplications(normalized);
      setError('');
    } catch (err) {
      setError('Failed to load applications');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = useMemo(() => {
    if (!searchQuery.trim()) return applications;
    const query = searchQuery.toLowerCase();
    return applications.filter((app) => {
      const fullName = `${app.firstName} ${app.lastName}`.toLowerCase();
      const email = (app.email || '').toLowerCase();
      const specialty = (app.areaOfSpecialty || '').toLowerCase();
      const status = (app.status || '').toLowerCase().replace('_', ' ');
      return (
        fullName.includes(query) ||
        email.includes(query) ||
        specialty.includes(query) ||
        status.includes(query)
      );
    });
  }, [applications, searchQuery]);

  const handleRowClick = (app) => {
    navigate(`/applications/${app.id}`);
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'approved':
        return badgeStyles.success;
      case 'rejected':
        return badgeStyles.danger;
      case 'under_review':
        return badgeStyles.warning;
      default:
        return badgeStyles.secondary;
    }
  };

  const columns = [
    {
      header: 'Name',
      accessor: 'firstName',
      render: (firstName, app) => `${firstName} ${app.lastName}`.trim() || 'N/A',
    },
    {
      header: 'Email',
      accessor: 'email',
    },
    {
      header: 'Specialty',
      accessor: 'areaOfSpecialty',
      render: (value) => value || '-',
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (status) => {
        const safeStatus = status || 'pending';
        return (
          <span
            style={{
              ...badgeStyles.base,
              ...getStatusBadgeStyle(safeStatus),
            }}
          >
            {safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1).replace('_', ' ')}
          </span>
        );
      },
    },
    {
      header: 'Date',
      accessor: 'createdAt',
      render: (date) => (date ? new Date(date).toLocaleDateString() : 'N/A'),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (id) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/applications/${id}`);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Layout title="Membership Applications">
      {error && (
        <div
          style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <SearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, specialty, or status..."
          style={{ maxWidth: '500px' }}
        />
      </div>

      <Table
        columns={columns}
        data={filteredApplications}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage="No applications found"
      />
    </Layout>
  );
};

export default Dashboard;